import {
  acquireUserOperation,
  isUserOperationActive,
  registerDevResourceCleanup,
  releaseUserOperation,
  runDevCleanups,
} from "./dev-cleanup";

const PAUSED_KEY = "__dwimDevEditingPaused";
const HMR_TAP_INSTALLED_KEY = "__dwimHmrTapInstalled";
const COMPILE_STATE_KEY = "__dwimDevEditingCompile";
const DISMISSED_COMPILE_EPOCH_KEY = "__dwimDevEditingDismissedCompileEpoch";
/** Stop suppressing late HMR events after resume if Turbopack never sends built/sync. */
const RESUME_SUPPRESS_MS = 15_000;
/** Clear a stuck inBuild flag if built/sync never arrives. */
const BUILD_RESET_MS = 8_000;

type DevEditingCompileState = {
  compileEpoch: number;
  inBuild: boolean;
  awaitingBuildCompletion: boolean;
  buildResetTimer: ReturnType<typeof setTimeout> | null;
  resumeSuppressTimer: ReturnType<typeof setTimeout> | null;
};

type ImportMetaWithHot = ImportMeta & {
  webpackHot?: { on: (event: string, handler: () => void) => void };
};

type NodeModuleWithHot = NodeModule & {
  hot?: {
    addStatusHandler: (handler: (status: string) => void) => void;
  };
};

type HmrMessage = { type?: string };

function isDevBrowser(): boolean {
  return process.env.NODE_ENV !== "production" && typeof window !== "undefined";
}

function readPaused(): boolean {
  return (globalThis as typeof globalThis & { [PAUSED_KEY]?: boolean })[PAUSED_KEY] === true;
}

function writePaused(paused: boolean): void {
  (globalThis as typeof globalThis & { [PAUSED_KEY]?: boolean })[PAUSED_KEY] = paused;
}

function readCompileState(): DevEditingCompileState {
  const root = globalThis as typeof globalThis & {
    [COMPILE_STATE_KEY]?: DevEditingCompileState;
  };
  if (!root[COMPILE_STATE_KEY]) {
    root[COMPILE_STATE_KEY] = {
      compileEpoch: 0,
      inBuild: false,
      awaitingBuildCompletion: false,
      buildResetTimer: null,
      resumeSuppressTimer: null,
    };
  }
  return root[COMPILE_STATE_KEY];
}

function readDismissedCompileEpoch(): number {
  const dismissed =
    (globalThis as typeof globalThis & { [DISMISSED_COMPILE_EPOCH_KEY]?: number })[
      DISMISSED_COMPILE_EPOCH_KEY
    ] ?? 0;
  return Number.isFinite(dismissed) && dismissed > 0 ? dismissed : 0;
}

function dismissCurrentCompileEpoch(): void {
  const state = readCompileState();
  (globalThis as typeof globalThis & { [DISMISSED_COMPILE_EPOCH_KEY]?: number })[
    DISMISSED_COMPILE_EPOCH_KEY
  ] = state.compileEpoch;
  state.awaitingBuildCompletion = true;
  if (state.resumeSuppressTimer) {
    clearTimeout(state.resumeSuppressTimer);
  }
  state.resumeSuppressTimer = setTimeout(() => {
    state.awaitingBuildCompletion = false;
    state.resumeSuppressTimer = null;
  }, RESUME_SUPPRESS_MS);
}

function clearBuildResetTimer(state: DevEditingCompileState): void {
  if (state.buildResetTimer) {
    clearTimeout(state.buildResetTimer);
    state.buildResetTimer = null;
  }
}

function markBuildStarted(): void {
  const state = readCompileState();
  state.inBuild = true;
  state.awaitingBuildCompletion = false;
  if (state.resumeSuppressTimer) {
    clearTimeout(state.resumeSuppressTimer);
    state.resumeSuppressTimer = null;
  }
  clearBuildResetTimer(state);
  state.buildResetTimer = setTimeout(() => {
    state.inBuild = false;
    state.buildResetTimer = null;
  }, BUILD_RESET_MS);
}

function markBuildFinished(): void {
  const state = readCompileState();
  state.inBuild = false;
  state.awaitingBuildCompletion = false;
  clearBuildResetTimer(state);
  if (state.resumeSuppressTimer) {
    clearTimeout(state.resumeSuppressTimer);
    state.resumeSuppressTimer = null;
  }
}

function bumpCompileEpoch(): void {
  readCompileState().compileEpoch += 1;
}

function noteCompileActivity(): void {
  const state = readCompileState();
  if (state.inBuild || state.awaitingBuildCompletion) {
    return;
  }
  bumpCompileEpoch();
}

function shouldPauseForCurrentCompile(): boolean {
  return readCompileState().compileEpoch > readDismissedCompileEpoch();
}

/** Suppress dev editing pause while Improve / Keep is running (long OCR / model work). */
export function setImproveInProgress(active: boolean): void {
  if (!isDevBrowser()) {
    return;
  }
  if (active) {
    acquireUserOperation();
  } else {
    releaseUserOperation();
  }
}

const listeners = new Set<(paused: boolean) => void>();

export function isDevEditingPaused(): boolean {
  return isDevBrowser() && readPaused();
}

export function enterDevEditingPause(): void {
  if (
    !isDevBrowser() ||
    readPaused() ||
    isUserOperationActive() ||
    !shouldPauseForCurrentCompile()
  ) {
    return;
  }
  writePaused(true);
  void runDevCleanups();
  for (const listener of listeners) {
    listener(true);
  }
}

/** Leave pause mode without a full page reload (HMR already applied). */
export function exitDevEditingPause(): void {
  if (!isDevBrowser() || !readPaused()) {
    return;
  }
  writePaused(false);
  dismissCurrentCompileEpoch();
  for (const listener of listeners) {
    listener(false);
  }
}

export function subscribeDevEditingPause(listener: (paused: boolean) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function reloadAfterDevEdit(): void {
  writePaused(false);
  dismissCurrentCompileEpoch();
  window.location.reload();
}

function parseHmrMessage(data: unknown): HmrMessage | null {
  if (typeof data !== "string") {
    return null;
  }
  try {
    return JSON.parse(data) as HmrMessage;
  } catch {
    return null;
  }
}

function installNextHmrWebSocketTap(): void {
  const root = globalThis as typeof globalThis & { [HMR_TAP_INSTALLED_KEY]?: boolean };
  if (root[HMR_TAP_INSTALLED_KEY] || typeof WebSocket === "undefined") {
    return;
  }
  root[HMR_TAP_INSTALLED_KEY] = true;

  const OriginalWebSocket = WebSocket;
  let buildingTimer: ReturnType<typeof setTimeout> | null = null;
  let sawUpdateDuringBuild = false;

  const clearBuildingTimer = () => {
    if (buildingTimer) {
      clearTimeout(buildingTimer);
      buildingTimer = null;
    }
  };

  const TapWebSocket = function TapWebSocket(
    url: string | URL,
    protocols?: string | string[],
  ) {
    const ws = new OriginalWebSocket(url, protocols);
    const urlStr = typeof url === "string" ? url : url.toString();
    if (!urlStr.includes("/_next/hmr")) {
      return ws;
    }

    ws.addEventListener("message", (event) => {
      const message = parseHmrMessage(event.data);
      if (!message?.type) {
        return;
      }

      switch (message.type) {
        case "building": {
          markBuildStarted();
          bumpCompileEpoch();
          sawUpdateDuringBuild = false;
          clearBuildingTimer();
          enterDevEditingPause();
          buildingTimer = setTimeout(() => {
            buildingTimer = null;
            if (sawUpdateDuringBuild) {
              enterDevEditingPause();
            }
          }, 120);
          break;
        }
        case "turbopack-message":
        case "clientChanges":
        case "serverComponentChanges":
          noteCompileActivity();
          sawUpdateDuringBuild = true;
          enterDevEditingPause();
          break;
        case "built":
        case "sync":
          markBuildFinished();
          if (sawUpdateDuringBuild) {
            enterDevEditingPause();
          }
          clearBuildingTimer();
          break;
        default:
          break;
      }
    });

    return ws;
  } as unknown as typeof WebSocket;

  TapWebSocket.prototype = OriginalWebSocket.prototype;
  Object.assign(TapWebSocket, OriginalWebSocket);
  globalThis.WebSocket = TapWebSocket;
}

function installWebpackHotPauseHooks(): void {
  const moduleHot = typeof module !== "undefined" ? (module as NodeModuleWithHot).hot : undefined;
  let webpackReady = false;
  moduleHot?.addStatusHandler?.((status) => {
    if (status === "ready") {
      webpackReady = true;
      return;
    }
    if (webpackReady && (status === "prepare" || status === "dispose")) {
      noteCompileActivity();
      enterDevEditingPause();
    }
  });

  const importMetaHot = (import.meta as ImportMetaWithHot).webpackHot;
  importMetaHot?.on?.("webpackInvalidated", () => {
    noteCompileActivity();
    enterDevEditingPause();
  });
}

/** Install dev-only hooks that pause the app while source files are being edited. */
export function installDevEditingPauseHooks(): void {
  if (!isDevBrowser()) {
    return;
  }

  installNextHmrWebSocketTap();
  installWebpackHotPauseHooks();
  registerDevResourceCleanup("dev-editing-pause", () => {
    enterDevEditingPause();
  });
}
